import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Download, FileText, RefreshCw, ShieldAlert, TrendingUp } from 'lucide-react'
import {
  ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { ActiveFilters, DateRange, FilterBar, MultiSelect, SearchInput } from '@/shared/ui/filters'
import { CursorPager } from '@/shared/ui/CursorPager'
import { EmptyState, ErrorStrip, Forbidden, LoadingRows, Note } from '@/shared/ui/states'
import { useCursorPage } from '@/shared/hooks/useCursorPage'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { explain } from '@/shared/api/problem'
import { fmtBytes, fmtDateTime, fmtNumber, humanise, shortId } from '@/shared/lib/format'
import { countActive } from '@/shared/lib/query'
import { cn } from '@/lib/utils'
import {
  auditFacets, auditSummary, exportAudit, listAudit, type AuditFilters,
} from './api'
import { AuditDetailDrawer } from './AuditDetailDrawer'
import { CategoryBadge, OutcomeBadge } from './badges'
import type { AuditEntryResponse } from '@/shared/api/types'

/**
 * System Audit Log — dedicated page.
 *
 * PollSphere shipped this as its own screen rather than an administration tab,
 * and the shape follows: KPI strip, filter row, dense grid, row-click drawer.
 * The bundle carries no audit module to copy, so the composition is built from
 * the ported `erp/primitives` at PollSphere's own sizes.
 *
 * Filters live in the URL, so a filtered view is a link someone can send.
 */
export function AuditLogPage() {
  const allowed = useCan('audit:read')
  const canExport = useCan('audit:export')
  const [params, setParams] = useSearchParams()
  const [selected, setSelected] = useState<AuditEntryResponse | null>(null)
  const [exportNote, setExportNote] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  // ── Filters, mirrored into the query string ───────────────────────────────
  const filters = useMemo<AuditFilters>(() => ({
    action: params.getAll('action'),
    actorId: params.getAll('actorId'),
    entityType: params.getAll('entityType'),
    entityId: params.get('entityId') ?? '',
    category: params.getAll('category'),
    outcome: params.getAll('outcome'),
    fromDay: params.get('from') ?? '',
    toDay: params.get('to') ?? '',
    q: params.get('q') ?? '',
    requestId: params.get('requestId') ?? '',
    ip: params.get('ip') ?? '',
  }), [params])

  const [draftQ, setDraftQ] = useState(filters.q)
  const debouncedQ = useDebounced(draftQ, 300)

  // Keep the box in step when the URL changes underneath (back button, reset).
  useEffect(() => { setDraftQ(filters.q) }, [filters.q])

  const patch = useCallback((next: Partial<AuditFilters>) => {
    const merged = { ...filters, ...next }
    const sp = new URLSearchParams()
    merged.action.forEach((v) => sp.append('action', v))
    merged.actorId.forEach((v) => sp.append('actorId', v))
    merged.entityType.forEach((v) => sp.append('entityType', v))
    merged.category.forEach((v) => sp.append('category', v))
    merged.outcome.forEach((v) => sp.append('outcome', v))
    if (merged.entityId) sp.set('entityId', merged.entityId)
    if (merged.fromDay) sp.set('from', merged.fromDay)
    if (merged.toDay) sp.set('to', merged.toDay)
    if (merged.q) sp.set('q', merged.q)
    if (merged.requestId) sp.set('requestId', merged.requestId)
    if (merged.ip) sp.set('ip', merged.ip)
    setParams(sp, { replace: true })
  }, [filters, setParams])

  useEffect(() => {
    if (debouncedQ !== filters.q) patch({ q: debouncedQ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ])

  // A stable key so the cursor hook resets on any filter change, and only then.
  const filterKey = JSON.stringify(filters)

  // ── Data ──────────────────────────────────────────────────────────────────
  const list = useCursorPage<AuditEntryResponse>(
    (cursor) => listAudit(filters, cursor, 50),
    [filterKey],
  )
  const summary = useLoad(() => auditSummary(filters), [filterKey])
  // Facets are filter-independent and change slowly — loaded once.
  const facets = useLoad(() => auditFacets(), [])

  const activeCount = countActive({
    action: filters.action, actorId: filters.actorId, entityType: filters.entityType,
    entityId: filters.entityId, category: filters.category, outcome: filters.outcome,
    from: filters.fromDay, to: filters.toDay, q: filters.q,
    requestId: filters.requestId, ip: filters.ip,
  })

  const clearAll = () => {
    setDraftQ('')
    setParams(new URLSearchParams(), { replace: true })
  }

  async function runExport() {
    setExporting(true)
    setExportNote(null)
    try {
      const res = await exportAudit(filters)
      setExportNote(
        `Exported ${fmtNumber(res.rows)} rows to ${res.filename} (${fmtBytes(res.sizeBytes)})`
        + (res.truncated ? ' — capped, narrow the filter for the full set.' : '.'),
      )
    } catch (err) {
      setExportNote(explain(err))
    } finally {
      setExporting(false)
    }
  }

  if (!allowed) return <Forbidden what="the audit log" />

  const s = summary.data
  const f = facets.data
  const opt = (v: string) => ({ value: v, label: humanise(v) })

  return (
    <div className="space-y-4">
      <SectionHeader
        title="System Audit Log"
        description="Append-only record of every security, data and configuration event. Entries cannot be edited or deleted."
        right={
          <>
            {canExport && (
              <button className="erp-btn" onClick={runExport} disabled={exporting} aria-label="Export CSV">
                <Download className={cn('h-3.5 w-3.5', exporting && 'animate-pulse')} />
                <span className="axis-hide-sm">Export CSV</span>
              </button>
            )}
            <button className="erp-btn" onClick={() => { list.reload(); summary.reload() }} aria-label="Refresh">
              <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
            </button>
          </>
        }
      />

      {exportNote && (
        <Note tone={exportNote.startsWith('Exported') ? 'good' : 'bad'} onDismiss={() => setExportNote(null)}>
          {exportNote}
        </Note>
      )}

      {/* Cards reflect the CURRENT filter — the summary endpoint takes the same
          params as the list, so they never disagree with the grid below. */}
      <KpiStrip>
        <MetricCard
          label="Total events"
          value={s ? fmtNumber(s.total) : '—'}
          sub={activeCount > 0 ? 'matching current filter' : 'all time'}
          accent
        />
        <MetricCard label="Today" value={s ? fmtNumber(s.today) : '—'} sub="Africa/Nairobi" />
        <MetricCard label="Last 7 days" value={s ? fmtNumber(s.last7Days) : '—'} />
        <MetricCard
          label="Failures & denials"
          value={s ? fmtNumber(s.failures) : '—'}
          sub={s && s.total > 0 ? `${((s.failures / s.total) * 100).toFixed(1)}% of events` : undefined}
        />
        <MetricCard label="Distinct actors" value={s ? fmtNumber(s.distinctActors) : '—'} />
      </KpiStrip>

      <FilterBar
        activeCount={activeCount}
        right={<ActiveFilters count={activeCount} onClear={clearAll} />}
      >
        <SearchInput
          value={draftQ}
          onChange={setDraftQ}
          placeholder="Search action, actor or entity…"
        />
        <MultiSelect
          label="Action"
          options={(f?.actions ?? []).map(opt)}
          selected={filters.action}
          onChange={(action) => patch({ action })}
        />
        <MultiSelect
          label="Entity"
          options={(f?.entityTypes ?? []).map(opt)}
          selected={filters.entityType}
          onChange={(entityType) => patch({ entityType })}
        />
        <MultiSelect
          label="Category"
          options={(f?.categories ?? []).map(opt)}
          selected={filters.category}
          onChange={(category) => patch({ category })}
        />
        <MultiSelect
          label="Outcome"
          options={(f?.outcomes ?? []).map(opt)}
          selected={filters.outcome}
          onChange={(outcome) => patch({ outcome })}
        />
        <MultiSelect
          label="Actor"
          options={(f?.topActors ?? []).map((a) => ({ value: a.id, label: a.label, count: a.count }))}
          selected={filters.actorId}
          onChange={(actorId) => patch({ actorId })}
        />
        <DateRange
          from={filters.fromDay}
          to={filters.toDay}
          onChange={({ from, to }) => patch({ fromDay: from, toDay: to })}
        />
      </FilterBar>

      {/* Two secondary breakdowns. Bars, not a chart library — the whole page
          would otherwise pull in a charting dependency for two lists. */}
      {s && (s.topActions.length > 0 || Object.keys(s.byCategory).length > 0) && (
        <div className="axis-hide-sm grid gap-3 md:grid-cols-2">
          <Panel title="Top actions" description="Most frequent events in the current filter" dense>
            <Breakdown
              icon={<TrendingUp className="h-3.5 w-3.5" />}
              rows={s.topActions.map((a) => ({ label: a.action, count: a.count }))}
              onPick={(label) => patch({ action: [label] })}
            />
          </Panel>
          <Panel title="By category" description="Where the activity is coming from" dense>
            <Breakdown
              icon={<ShieldAlert className="h-3.5 w-3.5" />}
              rows={Object.entries(s.byCategory)
                .map(([label, count]) => ({ label, count }))
                .sort((a, b) => b.count - a.count)}
              onPick={(label) => patch({ category: [label] })}
            />
          </Panel>
        </div>
      )}

      <Panel bodyClassName="p-0">
        <ErrorStrip message={list.error} />
        <ErpTable
          head={
            <>
              <Th>Occurred</Th>
              <Th>Action</Th>
              <Th className="axis-hide-sm">Category</Th>
              <Th>Outcome</Th>
              <Th>Actor</Th>
              <Th className="axis-hide-sm">Entity</Th>
              <Th className="axis-hide-sm">Changes</Th>
              <Th className="axis-hide-sm">IP</Th>
            </>
          }
        >
          {list.loading && list.rows.length === 0 ? (
            <LoadingRows colSpan={8} />
          ) : list.rows.length === 0 ? (
            <tr>
              <td colSpan={8}>
                <EmptyState
                  icon={<FileText className="h-4 w-4" />}
                  title={activeCount > 0 ? 'No events match this filter' : 'No audit events yet'}
                  hint={activeCount > 0
                    ? 'Widen the date range or clear a filter.'
                    : 'Events appear here as soon as anyone signs in or changes a record.'}
                  action={activeCount > 0
                    ? <button className="erp-btn" onClick={clearAll}>Clear filters</button>
                    : undefined}
                />
              </td>
            </tr>
          ) : (
            list.rows.map((e) => (
              <tr
                key={e.id}
                onClick={() => setSelected(e)}
                className="hover:bg-muted/40 cursor-pointer"
                tabIndex={0}
                onKeyDown={(ev) => { if (ev.key === 'Enter') setSelected(e) }}
              >
                <Td className="whitespace-nowrap erp-metric">{fmtDateTime(e.occurredAt)}</Td>
                <Td><span className="font-medium">{e.action}</span></Td>
                <Td className="axis-hide-sm"><CategoryBadge category={e.category} /></Td>
                <Td><OutcomeBadge outcome={e.outcome} /></Td>
                <Td>
                  {e.actorLabel
                    ? <span className="truncate">{e.actorLabel}</span>
                    : e.actorId
                      ? <span className="font-mono text-erp-xs">{shortId(e.actorId)}</span>
                      : <span className="text-muted-foreground">System</span>}
                </Td>
                <Td className="axis-hide-sm">
                  {e.entityLabel ?? (e.entityType
                    ? <span className="font-mono text-erp-xs text-muted-foreground">{e.entityType}</span>
                    : '—')}
                </Td>
                <Td className="axis-hide-sm">
                  {e.changes && e.changes.length > 0
                    ? <span className="inline-flex items-center px-1.5 py-0.5 rounded-md border border-chocolate/30 bg-chocolate-soft/40 text-chocolate text-erp-xs font-medium">
                        {e.changes.length} field{e.changes.length === 1 ? '' : 's'}
                      </span>
                    : <span className="text-muted-foreground">—</span>}
                </Td>
                <Td className="axis-hide-sm font-mono text-erp-xs">{e.ip ?? '—'}</Td>
              </tr>
            ))
          )}
        </ErpTable>

        <CursorPager
          page={list.page}
          count={list.rows.length}
          hasMore={list.hasMore}
          canGoBack={list.canGoBack}
          onBack={list.back}
          onNext={list.next}
          loading={list.loading}
          unit="events"
        />
      </Panel>

      <AuditDetailDrawer entry={selected} onClose={() => setSelected(null)} />
    </div>
  )
}

/** Proportional bar list. Clicking a row filters the grid to it. */
function Breakdown({
  rows, onPick, icon,
}: {
  rows: { label: string; count: number }[]
  onPick: (label: string) => void
  icon?: React.ReactNode
}) {
  const max = Math.max(1, ...rows.map((r) => r.count))
  if (rows.length === 0) {
    return <p className="text-erp-sm text-muted-foreground">Nothing recorded yet.</p>
  }
  return (
    <ul className="space-y-1">
      {rows.slice(0, 6).map((r) => (
        <li key={r.label}>
          <button
            type="button"
            onClick={() => onPick(r.label)}
            className="w-full text-left group"
          >
            <div className="flex items-center gap-2 text-erp-sm">
              <span className="text-muted-foreground shrink-0">{icon}</span>
              <span className="truncate group-hover:text-chocolate">{humanise(r.label)}</span>
              <span className="ml-auto erp-metric text-erp-xs text-muted-foreground">
                {fmtNumber(r.count)}
              </span>
            </div>
            <div className="mt-0.5 h-1 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-chocolate/70 group-hover:bg-chocolate"
                style={{ width: `${(r.count / max) * 100}%` }}
              />
            </div>
          </button>
        </li>
      ))}
    </ul>
  )
}
